import React from 'react';
import { ModalProvider } from 'react-modal-hook';
import { useSelector } from 'react-redux';
import { Redirect, Route, RouteProps, Switch } from 'react-router-dom';
import { ErrorBoundary } from 'components/ErrorBoundary/ErrorBoundary';
import { AppProvider } from 'contexts/app';
import { AuthService } from 'lib';
import {
  AcceptInvitation,
  AccountSettings,
  AccountSetupAddDateOfBirth,
  AccountSetupAddMissingIdentity,
  AccountSetupAvatar,
  AccountSetupComplete,
  AccountSetupLayout,
  AccountSetupNotifications,
  AccountSetupPaymentMethod,
  AdminDashboard,
  AuthAcceptInviteCreateAccount,
  AuthAcceptInviteLogin,
  AuthAcceptInviteLoginGoogle,
  AuthLogin,
  AuthLoginGoogle,
  AuthRemoteSession,
  CalculatorSettings,
  ConnectIntegrationConnect,
  ConnectIntegrationConnections,
  ConnectIntegrationLogin,
  ConnectIntegrationLoginGoogle,
  ConnectIntegrationSuccess,
  DaySegmentsSettings,
  EmployeeDashboard,
  ErrorScreen,
  IntegrationsSettings,
  InvitationAlreadyClaimed,
  InvitationExpired,
  InvitationInvalid,
  InvitationNotFound,
  LaborReview,
  McpConnect,
  NotificationsSettings,
  PasswordCreateNew,
  PasswordCreateNewAcceptInvite,
  PasswordLookup,
  PasswordLookupAcceptInvite,
  PayCalculatorTips,
  PaymentMethodSettings,
  PaymentSet,
  PaymentsScreen,
  PaymentSummary,
  ReportsAchHistory,
  ReportsBankReconciliation,
  ReportsEmployeePayrollTips,
  ReportsLedger,
  ReportsPaidPayments,
  ReportsPayrollTips,
  ReportsScreenLayout,
  SelectWorkflow,
  SettingsScreenLayout,
  SignUpAccountSecurity,
  SignUpCreateAccountEmail,
  SignUpCreateAccountGoogle,
  SignUpOptions,
  SiteSettingsScreenLayout,
  StandardUpload,
  TermsOfUse,
  UserDetail,
  Users,
  UsersAdd,
  WithholdingsSettings,
} from 'screens';
import { AccountRecoveryIdentity, AccountRecoveryInit } from 'screens/AccountRecovery';
import { EmployeePayoutDetails } from 'screens/EmployeeDashboard/EmployeePayments';
import { selectCurrentEmployee } from 'store/App';
import { selectCurrentUser } from 'store/User';
import { ActivePaymentsTab, EmployeeRole, EmployeesTab } from 'types';
import { PrivateRouteLayout } from './PrivateRouteLayout';
import routeConstants, { invitesPrefix } from './routeConstants';
import RouteMonitor from './RouteMonitor';
import Router from './Router';
import VersionMonitor from './VersionMonitor';

const ADMIN_ROLES = [EmployeeRole.ADMIN, EmployeeRole.SITE_ADMIN];
const MANAGEMENT_ROLES = [EmployeeRole.ADMIN, EmployeeRole.SITE_ADMIN, EmployeeRole.MANAGER];

enum RouteType {
  PUBLIC = 'public',
  PRIVATE = 'private',
}

interface IAppRoute extends RouteProps {
  type: RouteType;
  requiredRoles?: EmployeeRole[];
  remoteSession?: boolean;
  hideNav?: boolean; // Set to true to hide the left hand navigation
  hideHeader?: boolean;
  hideMakePayment?: boolean; // Set to true to hide the header's "Make Payment" button
  logout?: boolean; // Logout the user before routing
}

const AppRoute = ({ type, requiredRoles, remoteSession = true, path, children, hideNav, logout, hideHeader, hideMakePayment }: IAppRoute) => {
  if (type === RouteType.PUBLIC) {
    if (logout) AuthService.clearToken();
    return (
      <Route path={path} exact>
        {children}
      </Route>
    );
  }

  // Handle remote sessions (access denied)
  const currentUser = useSelector(selectCurrentUser());
  if (!remoteSession && currentUser?.remoteSession) {
    return (
      <Route path={path} exact>
        {' '}
        <ErrorScreen />
      </Route>
    ); // Show 404 if user does not have required access
  }

  return (
    <PrivateRoute path={path} requiredRoles={requiredRoles} hideNav={hideNav} hideHeader={hideHeader} hideMakePayment={hideMakePayment} exact>
      {children}
    </PrivateRoute>
  );
};

const PrivateRoute: React.FC<Partial<IAppRoute>> = ({ path, children, requiredRoles, hideNav, hideHeader, hideMakePayment }: Partial<IAppRoute>) => {
  const token = AuthService.getToken();

  if (!token) return <Redirect to={routeConstants.public.login} />;

  return (
    <Route path={path} exact>
      <PrivateRouteLayout hideNav={hideNav} hideHeader={hideHeader} hideMakePayment={hideMakePayment} requiredRoles={requiredRoles}>
        {children}
      </PrivateRouteLayout>
    </Route>
  );
};

export const AppRoutes: React.FC = () => (
  <Router>
    <ErrorBoundary boundaryName="AppRoot">
      <AppProvider>
        <ModalProvider>
          <RouteMonitor />
          <VersionMonitor>
            <Switch>
              {/* MCP Connect — OAuth authorization for Claude and other AI clients */}
              <AppRoute type={RouteType.PUBLIC} path={routeConstants.public.mcpConnect} exact>
                <McpConnect />
              </AppRoute>

              {/* Login  */}
              <AppRoute type={RouteType.PUBLIC} path={routeConstants.public.login} logout exact>
                <AuthLogin />
              </AppRoute>

              {/* Login Integrations */}
              <AppRoute type={RouteType.PUBLIC} path={routeConstants.public.connectIntegrationLogin} logout exact>
                <ConnectIntegrationLogin />
              </AppRoute>

              {/* Forgot Password  - Password Lookup */}
              <AppRoute type={RouteType.PUBLIC} path={routeConstants.public.resetPassword} exact>
                <PasswordLookup />
              </AppRoute>

              {/* Forgot Password - Reset Password */}
              <AppRoute type={RouteType.PUBLIC} path={routeConstants.public.resetPasswordCreateNew} exact>
                <PasswordCreateNew />
              </AppRoute>

              {/* Google Login - Callback  */}
              <AppRoute type={RouteType.PUBLIC} path={routeConstants.public.redirectGoogle.login} logout exact>
                <AuthLoginGoogle />
              </AppRoute>

              {/* Google Integration Login - Callback  */}
              <AppRoute type={RouteType.PUBLIC} path={routeConstants.public.redirectGoogle.integrationLogin} logout exact>
                <ConnectIntegrationLoginGoogle />
              </AppRoute>

              {/* Sign Up  */}
              <AppRoute type={RouteType.PUBLIC} path={routeConstants.public.signUp} logout exact>
                <SignUpOptions />
              </AppRoute>

              {/* Sign Up - With Email */}
              <AppRoute type={RouteType.PUBLIC} path={routeConstants.public.signUpEmail} exact>
                <SignUpCreateAccountEmail />
              </AppRoute>

              {/* Sign Up - Account Security */}
              <AppRoute type={RouteType.PUBLIC} path={routeConstants.public.accountSecurity} exact>
                <SignUpAccountSecurity />
              </AppRoute>

              {/* Sign Up - With Google - Callback */}
              <AppRoute type={RouteType.PUBLIC} path={routeConstants.public.redirectGoogle.signUp} exact>
                <SignUpCreateAccountGoogle />
              </AppRoute>

              {/* Account Recovery */}
              <AppRoute type={RouteType.PUBLIC} path={routeConstants.public.accountRecovery.root} exact>
                <AccountRecoveryInit />
              </AppRoute>

              {/* Account Recovery - Add Mobile */}
              <AppRoute type={RouteType.PUBLIC} path={routeConstants.public.accountRecovery.addMobileIdentity} exact>
                <AccountRecoveryIdentity />
              </AppRoute>

              {/* Invite - Not Found */}
              <AppRoute type={RouteType.PUBLIC} path={routeConstants.public.invites.notFound} logout exact>
                <InvitationNotFound />
              </AppRoute>

              {/* Invite - Expired */}
              <AppRoute type={RouteType.PUBLIC} path={routeConstants.public.invites.expired} logout exact>
                <InvitationExpired />
              </AppRoute>

              {/* Invite - Already Claimed */}
              <AppRoute type={RouteType.PUBLIC} path={routeConstants.public.invites.alreadyClaimed} logout exact>
                <InvitationAlreadyClaimed />
              </AppRoute>

              {/* Invite - Invalid */}
              <AppRoute type={RouteType.PUBLIC} path={routeConstants.public.invites.invalid} logout exact>
                <InvitationInvalid />
              </AppRoute>

              {/* Invite - Login & Accept */}
              <AppRoute type={RouteType.PUBLIC} path={routeConstants.public.invites.login} logout exact>
                <AuthAcceptInviteLogin />
              </AppRoute>

              {/* Invite - Login with Google (callback) & Accept */}
              <AppRoute type={RouteType.PUBLIC} path={routeConstants.public.invites.invitesRedirectGoogle.login} logout exact>
                <AuthAcceptInviteLoginGoogle />
              </AppRoute>

              {/* Invite - Reset password */}
              <AppRoute type={RouteType.PUBLIC} path={routeConstants.public.invites.resetPassword} logout exact>
                <PasswordLookupAcceptInvite />
              </AppRoute>

              {/* Invite - Create new password & Accept */}
              <AppRoute type={RouteType.PUBLIC} path={routeConstants.public.invites.resetPasswordCreateNew} logout exact>
                <PasswordCreateNewAcceptInvite />
              </AppRoute>

              {/* Invite - Create new password & Accept */}
              <AppRoute type={RouteType.PUBLIC} path={routeConstants.public.invites.resetPasswordCreateNew} logout exact>
                <PasswordCreateNewAcceptInvite />
              </AppRoute>

              {/* Invite - Create Account & Accept */}
              <AppRoute type={RouteType.PUBLIC} path={`${invitesPrefix}/:inviteToken`} logout exact>
                <AuthAcceptInviteCreateAccount />
              </AppRoute>

              {/* Terms of Use */}
              <AppRoute type={RouteType.PUBLIC} path={routeConstants.public.termsOfUse} exact>
                <TermsOfUse />
              </AppRoute>

              {/* Remote Session */}
              <AppRoute type={RouteType.PUBLIC} path={routeConstants.public.remoteSession} exact>
                <AuthRemoteSession />
              </AppRoute>

              {/* Dashboard */}
              <AppRoute type={RouteType.PRIVATE} path={routeConstants.private.rootPath} exact>
                {!useSelector(selectCurrentEmployee())?.organizationId ? <EmployeeDashboard /> : <AdminDashboard />}
              </AppRoute>

              {/* Dashboard Alias */}
              <Route path={routeConstants.private.dashboard}>
                <Redirect to={routeConstants.private.rootPath} />
              </Route>

              {/* Employee Settings */}
              <AppRoute type={RouteType.PRIVATE} path={[routeConstants.private.settingsAccount]} exact remoteSession={false}>
                <SettingsScreenLayout>
                  <AccountSettings />
                </SettingsScreenLayout>
              </AppRoute>

              {/* Employee - Accept Invitation */}
              <AppRoute type={RouteType.PRIVATE} path={routeConstants.private.acceptInvitation} hideNav exact>
                <AcceptInvitation />
              </AppRoute>

              {/* Employee -  Account Setup - Step 1 - Setup Notifications */}
              <AppRoute type={RouteType.PRIVATE} path={routeConstants.private.accountSetup.notifications} hideNav exact>
                <AccountSetupLayout currentStep={1}>
                  <AccountSetupNotifications />
                </AccountSetupLayout>
              </AppRoute>

              {/* Employee -  Account Setup - Step 2 - Setup Avatar */}
              <AppRoute type={RouteType.PRIVATE} path={routeConstants.private.accountSetup.avatar} hideNav exact>
                <AccountSetupLayout currentStep={2}>
                  <AccountSetupAvatar />
                </AccountSetupLayout>
              </AppRoute>

              {/* Employee -  Account Setup - Step 3 - Add Payment Method */}
              <AppRoute type={RouteType.PRIVATE} path={routeConstants.private.accountSetup.paymentMethod} hideNav exact>
                <AccountSetupLayout currentStep={3}>
                  <AccountSetupPaymentMethod />
                </AccountSetupLayout>
              </AppRoute>

              {/* Employee -  Account Setup - Step 4 - Complete Setup */}
              <AppRoute type={RouteType.PRIVATE} path={routeConstants.private.accountSetup.complete} hideNav exact>
                <AccountSetupComplete />
              </AppRoute>

              {/* Employee - Payouts */}
              <AppRoute type={RouteType.PRIVATE} path={`${routeConstants.private.employeePayoutDetails}/:paymentId`} exact>
                <EmployeePayoutDetails />
              </AppRoute>

              {/* Employee - Account Setup - Missing Identity */}
              <AppRoute type={RouteType.PRIVATE} path={routeConstants.private.accountSetup.missingIdentity} hideNav exact>
                <AccountSetupLayout currentStep={1} hideProgress>
                  <AccountSetupAddMissingIdentity />
                </AccountSetupLayout>
              </AppRoute>

              {/* Employee - Add DOB */}
              <AppRoute type={RouteType.PRIVATE} path={routeConstants.private.addDOB} hideNav exact>
                <AccountSetupAddDateOfBirth />
              </AppRoute>

              {/* Employee - Settings - Payment Method */}
              <AppRoute type={RouteType.PRIVATE} path={routeConstants.private.settingsPaymentMethod} exact>
                <SettingsScreenLayout>
                  <PaymentMethodSettings />
                </SettingsScreenLayout>
              </AppRoute>

              {/* Employee - Settings - Notifications */}
              <AppRoute type={RouteType.PRIVATE} path={routeConstants.private.settingsNotifications} exact>
                <SettingsScreenLayout>
                  <NotificationsSettings />
                </SettingsScreenLayout>
              </AppRoute>

              {/* Labor Review */}
              <AppRoute type={RouteType.PRIVATE} path={routeConstants.private.laborReview} hideNav hideMakePayment exact>
                <LaborReview />
              </AppRoute>

              {/* Admin - Payments Overview */}
              <AppRoute type={RouteType.PRIVATE} path={routeConstants.private.paymentsOverview} exact requiredRoles={MANAGEMENT_ROLES}>
                <PaymentsScreen activeTab={ActivePaymentsTab.OVERVIEW} />
              </AppRoute>

              {/* Admin - Payments Details */}
              <AppRoute type={RouteType.PRIVATE} path={routeConstants.private.paymentsDetail} exact requiredRoles={MANAGEMENT_ROLES}>
                <PaymentsScreen activeTab={ActivePaymentsTab.DETAILS} />
              </AppRoute>

              {/* Admin - Payments Review */}
              <AppRoute type={RouteType.PRIVATE} path={routeConstants.private.paymentsReview} exact requiredRoles={ADMIN_ROLES}>
                <PaymentsScreen activeTab={ActivePaymentsTab.REVIEW} />
              </AppRoute>

              {/* Admin - Payments Set */}
              <AppRoute type={RouteType.PRIVATE} path={routeConstants.private.paymentSet} exact requiredRoles={MANAGEMENT_ROLES}>
                <PaymentSet />
              </AppRoute>

              {/* Admin - Users - All Users */}
              <AppRoute type={RouteType.PRIVATE} path={routeConstants.private.employees} exact requiredRoles={MANAGEMENT_ROLES}>
                <Users activeTab={EmployeesTab.ALL} />
              </AppRoute>

              {/* Admin - Users - Needs Review */}
              <AppRoute type={RouteType.PRIVATE} path={routeConstants.private.employeesNeedingApproval} exact requiredRoles={MANAGEMENT_ROLES}>
                <Users activeTab={EmployeesTab.REVIEW} />
              </AppRoute>

              {/* Admin - Add User  */}
              <AppRoute type={RouteType.PRIVATE} path={routeConstants.private.addEmployee} exact requiredRoles={MANAGEMENT_ROLES}>
                <UsersAdd />
              </AppRoute>

              {/* Admin - User - Details */}
              <AppRoute type={RouteType.PRIVATE} path={routeConstants.private.employeeDetail} exact requiredRoles={MANAGEMENT_ROLES}>
                <UserDetail />
              </AppRoute>

              {/* Admin - Make Payment - Select Workflow */}
              <AppRoute type={RouteType.PRIVATE} path={routeConstants.private.makePayment} exact requiredRoles={MANAGEMENT_ROLES}>
                <SelectWorkflow />
              </AppRoute>

              {/* Admin - Make Payment - Tip Calculator */}
              <AppRoute type={RouteType.PRIVATE} path={`${routeConstants.private.makePaymentCalculator}/:step`} exact requiredRoles={MANAGEMENT_ROLES}>
                <PayCalculatorTips />
              </AppRoute>

              {/* Admin - Make Payment - Standard/Upload */}
              <AppRoute type={RouteType.PRIVATE} path={`${routeConstants.private.makePayment}/:workflow/:step`} exact requiredRoles={MANAGEMENT_ROLES}>
                <StandardUpload />
              </AppRoute>

              {/* Admin - Make Payment - Payment Summary */}
              <AppRoute type={RouteType.PRIVATE} path={routeConstants.private.makePaymentSummary} exact requiredRoles={MANAGEMENT_ROLES}>
                <PaymentSummary />
              </AppRoute>

              {/* Admin - Reports - Paid Payments */}
              <AppRoute type={RouteType.PRIVATE} path={routeConstants.private.reportsPaidPayments} exact requiredRoles={MANAGEMENT_ROLES}>
                <ReportsScreenLayout>
                  <ReportsPaidPayments />
                </ReportsScreenLayout>
              </AppRoute>

              {/* Admin - Reports - Payroll Tips */}
              <AppRoute type={RouteType.PRIVATE} path={routeConstants.private.reportsPayrollTips} exact requiredRoles={MANAGEMENT_ROLES}>
                <ReportsScreenLayout>
                  <ReportsPayrollTips />
                </ReportsScreenLayout>
              </AppRoute>

              {/* Admin - Reports - Payroll Tips - Employee View */}
              <AppRoute type={RouteType.PRIVATE} path={`${routeConstants.private.reportsEmployeePayrollTips}/:id/:startDate/:endDate`} exact requiredRoles={MANAGEMENT_ROLES}>
                <ReportsEmployeePayrollTips />
              </AppRoute>

              {/* Admin - Reports - Bank Reconciliation */}
              <AppRoute type={RouteType.PRIVATE} path={`${routeConstants.private.reportsBankReconciliation}/:option?`} exact requiredRoles={MANAGEMENT_ROLES}>
                <ReportsScreenLayout>
                  <ReportsBankReconciliation />
                </ReportsScreenLayout>
              </AppRoute>

              {/* Admin - Reports - Ledger */}
              <AppRoute type={RouteType.PRIVATE} path={`${routeConstants.private.reportsLedger}/:option?`} exact requiredRoles={MANAGEMENT_ROLES}>
                <ReportsScreenLayout>
                  <ReportsLedger />
                </ReportsScreenLayout>
              </AppRoute>

              {/* Admin - Reports - ACH History */}
              <AppRoute type={RouteType.PRIVATE} path={routeConstants.private.reportsAchHistory} exact requiredRoles={ADMIN_ROLES}>
                <ReportsScreenLayout>
                  <ReportsAchHistory />
                </ReportsScreenLayout>
              </AppRoute>

              {/* Admin - Settings - Calculator Settings */}
              <AppRoute type={RouteType.PRIVATE} path={`${routeConstants.private.settingsCalculator}/:tab?`} exact requiredRoles={MANAGEMENT_ROLES}>
                <SiteSettingsScreenLayout>
                  <CalculatorSettings />
                </SiteSettingsScreenLayout>
              </AppRoute>

              {/* Admin - Settings - Day Segments */}
              <AppRoute type={RouteType.PRIVATE} path={routeConstants.private.settingsDaySegments} exact requiredRoles={MANAGEMENT_ROLES}>
                <SiteSettingsScreenLayout>
                  <DaySegmentsSettings />
                </SiteSettingsScreenLayout>
              </AppRoute>

              {/* Admin - Settings - Withholdings */}
              <AppRoute type={RouteType.PRIVATE} path={`${routeConstants.private.settingsWithholdings}/:tab?`} exact requiredRoles={MANAGEMENT_ROLES}>
                <SiteSettingsScreenLayout>
                  <WithholdingsSettings />
                </SiteSettingsScreenLayout>
              </AppRoute>

              {/* Admin - Settings - Integrations */}
              <AppRoute type={RouteType.PRIVATE} path={routeConstants.private.settingsIntegrations} exact requiredRoles={MANAGEMENT_ROLES}>
                <SiteSettingsScreenLayout>
                  <IntegrationsSettings />
                </SiteSettingsScreenLayout>
              </AppRoute>

              {/* Integration Connections List */}
              <AppRoute type={RouteType.PRIVATE} path={routeConstants.private.connectIntegration.connections} exact hideNav hideHeader>
                <ConnectIntegrationConnections />
              </AppRoute>

              {/* Connect Integration Callback */}
              <AppRoute type={RouteType.PRIVATE} path={routeConstants.private.connectIntegration.callback} exact hideNav hideHeader>
                <ConnectIntegrationConnect />
              </AppRoute>

              {/* Connect Integration Success */}
              <AppRoute type={RouteType.PRIVATE} path={routeConstants.private.connectIntegration.success} exact hideNav hideHeader>
                <ConnectIntegrationSuccess />
              </AppRoute>

              {/* Route not found  */}
              <Route path="/*">
                <ErrorScreen />
              </Route>
            </Switch>
          </VersionMonitor>
        </ModalProvider>
      </AppProvider>
    </ErrorBoundary>
  </Router>
);
